/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { ClosedEnum } from "../../types/enums.js";

/**
 * Message context used for classification. Sliding windows classify the current message with preceding messages ordered by timestamp.
 */
export const InputStrategy = {
  SingleMessage: "single_message",
  SlidingWindow: "sliding_window",
} as const;
/**
 * Message context used for classification. Sliding windows classify the current message with preceding messages ordered by timestamp.
 */
export type InputStrategy = ClosedEnum<typeof InputStrategy>;

/**
 * How a sensor interprets its ordered signals: independently applicable labels, an exclusive choice, or ordered score levels.
 */
export const Mode = {
  MultiLabel: "multi_label",
  Exclusive: "exclusive",
  OrderedScore: "ordered_score",
} as const;
/**
 * How a sensor interprets its ordered signals: independently applicable labels, an exclusive choice, or ordered score levels.
 */
export type Mode = ClosedEnum<typeof Mode>;

export type CreateSigintSensorForm = {
  /**
   * Optional description; empty stores no value
   */
  description?: string | undefined;
  /**
   * Whether to enable evaluation; omission defaults to true
   */
  enabled?: boolean | undefined;
  /**
   * Message context used for classification. Sliding windows classify the current message with preceding messages ordered by timestamp.
   */
  inputStrategy?: InputStrategy | undefined;
  /**
   * Optional classification instructions; empty stores no value
   */
  instructions?: string | undefined;
  /**
   * Boolean CEL predicate, at most 4096 UTF-8 bytes; omission defaults to message.role == "user"
   */
  matchExpression?: string | undefined;
  /**
   * How a sensor interprets its ordered signals: independently applicable labels, an exclusive choice, or ordered score levels.
   */
  mode: Mode;
  /**
   * Display name; trimmed before enforcing the 1 to 200 character limit
   */
  name: string;
  /**
   * Initial ordered signal IDs. Omitted and [] both create an empty sensor.
   */
  signalIds?: Array<string> | undefined;
  /**
   * A short url-friendly label that uniquely identifies a resource.
   */
  slug?: string | undefined;
  /**
   * Total sliding window messages including the target; omission defaults to 5
   */
  windowSize?: number | undefined;
};

/** @internal */
export const InputStrategy$outboundSchema: z.ZodMiniEnum<typeof InputStrategy> =
  z.enum(InputStrategy);

/** @internal */
export const Mode$outboundSchema: z.ZodMiniEnum<typeof Mode> = z.enum(Mode);

/** @internal */
export type CreateSigintSensorForm$Outbound = {
  description?: string | undefined;
  enabled?: boolean | undefined;
  input_strategy?: string | undefined;
  instructions?: string | undefined;
  match_expression?: string | undefined;
  mode: string;
  name: string;
  signal_ids?: Array<string> | undefined;
  slug?: string | undefined;
  window_size?: number | undefined;
};

/** @internal */
export const CreateSigintSensorForm$outboundSchema: z.ZodMiniType<
  CreateSigintSensorForm$Outbound,
  CreateSigintSensorForm
> = z.pipe(
  z.object({
    description: z.optional(z.string()),
    enabled: z.optional(z.boolean()),
    inputStrategy: z.optional(InputStrategy$outboundSchema),
    instructions: z.optional(z.string()),
    matchExpression: z.optional(z.string()),
    mode: Mode$outboundSchema,
    name: z.string(),
    signalIds: z.optional(z.array(z.string())),
    slug: z.optional(z.string()),
    windowSize: z.optional(z.int()),
  }),
  z.transform((v) => {
    return remap$(v, {
      inputStrategy: "input_strategy",
      matchExpression: "match_expression",
      signalIds: "signal_ids",
      windowSize: "window_size",
    });
  }),
);

export function createSigintSensorFormToJSON(
  createSigintSensorForm: CreateSigintSensorForm,
): string {
  return JSON.stringify(
    CreateSigintSensorForm$outboundSchema.parse(createSigintSensorForm),
  );
}
